import { inject, Injectable } from '@angular/core';
import { CreatedTable } from '../extracted-data/extracted-table-creation.service';
import { ExtractedRow } from '../../models/extracted-values';
import { associateBy, mapValues } from '@ps-gui/shared-utils';
import { WordRect } from '@ps-gui/claims-terminal-shared';
import { parseValue } from '../../utils/type-parsers';
import { TableData } from '../../table-overlay/table-overlay';
import { OcrTableService } from '../ocr/ocr-table.service';
import { TableModel } from '../../models/extraction-models/extraction-models';

@Injectable({
  providedIn: 'root',
})
export class TableOverlayService {
  private readonly ocrTableService = inject(OcrTableService);

  createTable(
    tableData: TableData,
    tableModel: TableModel,
    ignoredRowIndices?: Set<number>,
  ): CreatedTable {
    const tableDataFromOcr = this.ocrTableService.getWordsInTable(
      tableData.pageId,
      tableData.lines.vertical().map((line) => line.offset()),
      tableData.lines.horizontal().map((line) => line.offset()),
    );

    // TODO: By now, this should be the same as
    // Object.fromEntries(
    //   tableData.columnHeaders()
    //     .map((header, index) => [header, index])
    //     .filter(([header, _]) => header !== undefined && header !== 'Unwichtig')
    // )
    const columnHeaderIndices = Object.fromEntries(
      tableData
        .columnHeaders()
        .filter((columnKey) => columnKey != undefined && columnKey != 'Unwichtig')
        .map((columnKey) => [
          columnKey,
          tableData.columnHeaders().findIndex((header) => header === columnKey),
        ]),
    ) as Record<string, number>;

    const tableRows: ExtractedRow[] = [];

    const tableFieldsByKeys = associateBy(tableModel.fieldModels, (field) => field.key);

    const suggestedFieldLocations: Record<string, WordRect> = {};

    for (let index = 0; index < tableDataFromOcr.length; index++) {
      if (ignoredRowIndices?.has(index)) continue;

      const row = tableDataFromOcr[index];

      const extractedRow = {
        rowData: mapValues(columnHeaderIndices, (columnKey, columnIndex) => {
          const { value, wordRect } = row[columnIndex];
          const parsedValue = parseValue(value, tableFieldsByKeys[columnKey]).value;
          const cellId = crypto.randomUUID();

          if (wordRect) {
            suggestedFieldLocations[cellId] = wordRect;
          }

          return { value: parsedValue, id: cellId };
        }),

        rowMembership: {
          tableId: tableData.id,
          originalTableRowIndex: index,
        },
      };

      tableRows.push(extractedRow);
    }

    return {
      tableData,
      rows: tableRows,
      suggestedFieldLocations,
    };
  }
}
